import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "../providers/supabase/supabase";

export type MarcoPriority = "P1" | "P2" | "P3";

/**
 * Mirrors mentorax.marco_assessments (26 columns). Both
 * mentorax.marco_latest_assessments and mentorax.marco_pending_assessments are
 * `select *` views over that table, so one row type covers both. Verified
 * against the live schema on 2026-10-19 (MX-MARCO-UI-002).
 */
export type MarcoAssessment = {
  id: string;
  contact_id: number;
  company_id: number | null;
  source_note_id: number | null;
  supersedes_id: string | null;
  run_id: string;
  mode: "BASELINE" | "FOUNDER_RESCAN";
  relationship_priority: MarcoPriority | null;
  commercial_priority: MarcoPriority;
  priority_rationale: string;
  icp_fit: "strong" | "partial" | "weak" | "unknown" | null;
  icp_fit_note: string | null;
  buying_intent: "current" | "historical" | "none" | "unknown" | null;
  buying_intent_note: string | null;
  potential_value: "starter_possible" | "unverified" | "none" | null;
  potential_value_note: string | null;
  recommended_offer: string | null;
  outreach_angle: string | null;
  assessed_on: string;
  assessed_at: string | null;
  decision_evidence: string | null;
  approval_state: "pending" | "founder_approved" | "rejected" | "superseded";
  approval_evidence: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
};

export type MarcoAssessmentsData = {
  /** At most one founder-approved assessment per contact (enforced by a unique index). */
  approved: MarcoAssessment | null;
  /** Pending assessments, newest first. Never shown as approved. */
  pending: MarcoAssessment[];
};

export const MARCO_ASSESSMENTS_QUERY_KEY = "marco-assessments";

/**
 * Postgres 42501 (insufficient privilege) or the matching PostgREST message.
 * Kept separate from generic failures so a permission problem is never shown
 * as "no assessment".
 */
export function isMarcoPermissionError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return (
    code === "42501" ||
    (typeof message === "string" && /permission denied/i.test(message))
  );
}

/**
 * Reads both assessment views for one contact through the browser Supabase
 * client. Errors are thrown, not converted to empty data, so a permission
 * failure (Postgres 42501) stays distinguishable from "no assessment".
 *
 * The card and the sidebar both call this hook with the same contactId, so
 * React Query serves them from one cached result under one shared key.
 */
export function useMarcoAssessments(contactId: number | undefined) {
  return useQuery({
    queryKey: [MARCO_ASSESSMENTS_QUERY_KEY, contactId],
    enabled: !!contactId,
    queryFn: async (): Promise<MarcoAssessmentsData> => {
      const mentorax = getSupabaseClient().schema("mentorax");

      const [approvedRes, pendingRes] = await Promise.all([
        mentorax
          .from("marco_latest_assessments")
          .select("*")
          .eq("contact_id", contactId),
        mentorax
          .from("marco_pending_assessments")
          .select("*")
          .eq("contact_id", contactId)
          .order("assessed_on", { ascending: false })
          .order("created_at", { ascending: false }),
      ]);

      if (approvedRes.error) throw approvedRes.error;
      if (pendingRes.error) throw pendingRes.error;

      const approvedRows = (approvedRes.data ?? []) as MarcoAssessment[];

      return {
        approved: approvedRows[0] ?? null,
        pending: (pendingRes.data ?? []) as MarcoAssessment[],
      };
    },
  });
}
